import { FC, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import {
  Add as AddIcon,
  Search as SearchIcon,
  TrackChanges as TrackChangesIcon,
  Visibility as VisibilityIcon,
} from "@mui/icons-material";
import DocumentTitle from "../../components/documentTitle/DocumentTitle";
import MainTitle from "../../components/mainTitle";
import ControlledTextField from "../../components/controlledComponents/ControlledTextField";
import { CONSTANTS } from "../../../utils/CONSTS";
import { pages } from "../../../utils/common";
import { useInstituteContext } from "../../../context/institute.context";
import { useObepListHook } from "../../../hooks/useObepHook";
import { ObepViewProps } from "./obep.interfaces";
import ManageObepDialog from "./manageObepDialog";

const PAGE_SIZE = 20;

const ObepView: FC<ObepViewProps> = () => {
  const navigate = useNavigate();
  const { selectedInstitute } = useInstituteContext();

  const [page, setPage] = useState(1);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  const { control, watch } = useForm({ defaultValues: { search: "" } });
  const searchQuery = watch("search");

  const { data, isLoading, error } = useObepListHook({
    page,
    limit: PAGE_SIZE,
  });

  // The BE list endpoint returns a bare array (no count envelope), so client-side
  // search and program filtering operate over the page that is currently loaded.
  const filteredRecords = useMemo(() => {
    const records = data ?? [];
    return records.filter((record) => {
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        const haystack = [
          record.obepName,
          record.description ?? "",
          record.measurements,
        ]
          .join(" ")
          .toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      return true;
    });
  }, [data, searchQuery]);

  const fetchedCount = data?.length ?? 0;
  const hasNextPage = fetchedCount === PAGE_SIZE;

  if (!selectedInstitute) {
    return (
      <>
        <DocumentTitle title={CONSTANTS.title.academics} />
        <Box p={3} data-testid="obep-view">
          <MainTitle title="OBEP" />
          <Alert severity="info" data-testid="obep-no-institute">
            Please select an institute from the dashboard to view OBEP records.
          </Alert>
        </Box>
      </>
    );
  }

  return (
    <>
      <DocumentTitle title={CONSTANTS.title.academics} />
      <Grid container spacing={3} data-testid="obep-view">
        <Grid item xs={12}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            sx={{ mb: 2 }}
          >
            <MainTitle title="Outcome-Based Education Policies" />
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              size="large"
              onClick={() => setCreateDialogOpen(true)}
              data-testid="add-obep-button"
            >
              Add OBEP Record
            </Button>
          </Box>
        </Grid>

        <Grid item xs={12}>
          <Card data-testid="obep-filters-card">
            <CardContent>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12}>
                  <ControlledTextField
                    control={control}
                    name="search"
                    id="obep-search"
                    label="Search"
                    isGridItem={false}
                    fullWidth
                    placeholder="Search by name, description, measurements..."
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    }}
                    inputProps={{ "data-testid": "obep-search-input" }}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {isLoading ? (
          <Grid item xs={12}>
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              minHeight="400px"
              data-testid="obep-loading"
            >
              <CircularProgress />
            </Box>
          </Grid>
        ) : error ? (
          <Grid item xs={12}>
            <Alert severity="error" data-testid="obep-error">
              Failed to load OBEP records. Please try again later.
            </Alert>
          </Grid>
        ) : filteredRecords.length === 0 ? (
          <Grid item xs={12}>
            <Card data-testid="obep-empty-state">
              <CardContent>
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                  minHeight="200px"
                  gap={2}
                >
                  <TrackChangesIcon
                    sx={{ fontSize: 64, color: "text.secondary" }}
                  />
                  <Typography variant="h6" color="text.secondary">
                    {fetchedCount === 0
                      ? "No OBEP records configured"
                      : "No OBEP records match your filters"}
                  </Typography>
                  {fetchedCount === 0 && (
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={() => setCreateDialogOpen(true)}
                      data-testid="add-obep-empty-button"
                    >
                      Add OBEP Record
                    </Button>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ) : (
          <>
            <Grid item xs={12}>
              <Typography
                variant="h6"
                gutterBottom
                data-testid="obep-count"
              >
                OBEP Records ({filteredRecords.length})
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <TableContainer
                component={Paper}
                data-testid="obep-table-container"
              >
                <Table data-testid="obep-table">
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Measurements</TableCell>
                      <TableCell>Created</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredRecords.map((record) => (
                      <TableRow
                        key={record.obepId}
                        hover
                        data-testid={`obep-row-${record.obepId}`}
                      >
                        <TableCell>
                          <Typography variant="body1" fontWeight="medium">
                            {record.obepName}
                          </Typography>
                          {record.description && (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{
                                display: "-webkit-box",
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: "vertical",
                                overflow: "hidden",
                                mt: 0.5,
                              }}
                            >
                              {record.description}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>{record.measurements}</TableCell>
                        <TableCell>
                          {new Date(record.createdAt).toLocaleDateString()}
                        </TableCell>
                        <TableCell align="right">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() =>
                              navigate(
                                `${pages.academics.path}/obep/${record.obepId}`,
                              )
                            }
                            data-testid={`view-obep-${record.obepId}`}
                          >
                            <VisibilityIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
            <Grid item xs={12}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mt: 2 }}
              >
                <Typography
                  variant="body2"
                  color="text.secondary"
                  data-testid="obep-page-indicator"
                >
                  Page {page} · Showing {fetchedCount} record
                  {fetchedCount === 1 ? "" : "s"} on this page
                </Typography>
                <Stack direction="row" spacing={1}>
                  <Button
                    variant="outlined"
                    size="small"
                    disabled={page === 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    data-testid="obep-prev-page"
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    disabled={!hasNextPage}
                    onClick={() => setPage((p) => p + 1)}
                    data-testid="obep-next-page"
                  >
                    Next
                  </Button>
                </Stack>
              </Stack>
            </Grid>
          </>
        )}
      </Grid>

      <ManageObepDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
      />
    </>
  );
};

export default ObepView;
